/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 *
 * TableMenu — Per-row actions dropdown for EOS transfer media study list.
 * Migrated from: lego/page/eos/transfer-media/TableMenu.jsx
 */

import { useState, useEffect } from 'react'
import { useModal } from '../../../contexts/ModalContext'
import ActionsMenu from '../../../components/shared/ActionsMenu'
import EosStudyInfoDialog from './EosStudyInfoDialog'
import ConfirmUpdateEosInfo from './ConfirmUpdateEosInfo'
import ConfirmRemoveStudy from './ConfirmRemoveStudy'
import PdfGenStudyDetails from './PdfGenStudyDetails'
import ArchiveStudyModal from './ArchiveStudyModal'
import type { TableMenuProps } from './types'

const ADMIN_ROLE = 'ADMIN'

const TableMenu: React.FC<TableMenuProps> = ({ item, isPdfgenStudy, userRole, isPdrFlow, reTransfer }) => {
  const { showModal } = useModal()
  const [items, setItems] = useState<
    Array<{
      value: string
      action: () => void
      disabled?: boolean
    }>
  >([
    {
      value: 'LOADING...',
      action: () => {},
      disabled: true
    }
  ])
  const [isOpen, setIsOpen] = useState(false)

  useEffect(() => {
    const menu: Array<{
      value: string
      action: () => void
      disabled?: boolean
    }> = []

    if (item?.archived || isPdrFlow) {
      menu.push({
        value: 'EOS_TRANSFER_MEDIA_STUDY_DETAILS_VIEW',
        action: () => {
          showModal(
            isPdfgenStudy ? (
              <PdfGenStudyDetails ftpId={item.id!} isReview={true} isPdrFlow={isPdrFlow} />
            ) : (
              <EosStudyInfoDialog ftpId={item.id!} preventAutoTransferMedia={true} isReview={true} />
            ),
            { keepParentModalsOpen: true }
          )
        }
      })
    }

    if (item.id && !isPdfgenStudy && !item?.archived && !isPdrFlow) {
      menu.push({
        value: 'EOS_TRANSFER_MEDIA_STUDY_DETAILS_EDIT',
        action: () => {
          showModal(<EosStudyInfoDialog ftpId={item.id!} preventAutoTransferMedia={true} />, {
            keepParentModalsOpen: true
          })
        }
      })
    }

    if (isPdfgenStudy && !item?.archived && !isPdrFlow) {
      menu.push({
        value: 'EOS_TRANSFER_MEDIA_STUDY_DETAILS_EDIT',
        action: () => {
          showModal(<PdfGenStudyDetails ftpId={item.id!} />, { keepParentModalsOpen: true })
        }
      })
    }

    if (item.studyFolderId && !item?.archived) {
      menu.push({
        value: 'EOS_TRANSFER_MEDIA_STUDY_DETAILS_REFRESH',
        action: () => {
          showModal(<ConfirmUpdateEosInfo ftpConfig={item} isPdrFlow={isPdrFlow} />, {
            keepParentModalsOpen: true
          })
        }
      })
    }

    if (item.studyFolderId && !item?.archived && userRole === ADMIN_ROLE && !isPdrFlow) {
      menu.push({
        value: 'ARCHIVE_STUDY',
        action: () => {
          showModal(<ArchiveStudyModal ftpConfig={item} />, { keepParentModalsOpen: true })
        }
      })
    }

    if (isPdrFlow) {
      menu.push({
        value: 'MENU_ITEM_RETRANSFER',
        action: () => {
          if (item?.id) {
            reTransfer(item?.id)
          }
        }
      })
    }

    menu.push({
      value: 'ACTION_REMOVE',
      action: () => {
        showModal(<ConfirmRemoveStudy ftpConfig={item} isPdrFlow={isPdrFlow} />, {
          keepParentModalsOpen: true
        })
      }
    })

    setItems(menu)
  }, [item, isOpen])

  return (
    <ActionsMenu
      menuId={`action-menu-ftp-${item.id}`}
      onToggle={(open: boolean) => {
        setIsOpen(open)
      }}
      items={items}
    />
  )
}

export default TableMenu
